import * as Yup from 'yup';
import i18n from '@/i18n';
import { TranslationNS } from '@gokrew/models/constants';
import {
  DEFAULT_LOCATION_COUNTRY,
  LOCATION_CITY_MAX_LENGTH,
  PROVINCE_REQUIRED_COUNTRIES,
  type LocationCountry,
  type LocationFormValues,
  type LocationPayload,
  type LocationProvince,
} from '@gokrew/models/location';
import type { ComboBoxOption } from '@/components/ComboBox';

/** Whether a location in this country must name a province or state. */
export const isProvinceRequired = (countryCode?: string | null): boolean =>
  !!countryCode &&
  (PROVINCE_REQUIRED_COUNTRIES as readonly string[]).includes(
    countryCode.toUpperCase()
  );

const baseLanguage = (locale: string) =>
  (locale || 'en').toLowerCase().split('-')[0];

const regionNames = new Map<string, Intl.DisplayNames | null>();

/**
 * A country's name in the reader's language ("Allemagne" for DE in French),
 * from the browser's own data; the API's English name when it has none.
 */
export function countryDisplayName(
  country: LocationCountry,
  locale: string
): string {
  const language = baseLanguage(locale);
  if (!regionNames.has(language)) {
    try {
      regionNames.set(
        language,
        new Intl.DisplayNames([language], { type: 'region' })
      );
    } catch {
      regionNames.set(language, null);
    }
  }
  try {
    const name = regionNames.get(language)?.of(country.code.toUpperCase());
    return name && name !== country.code.toUpperCase() ? name : country.name;
  } catch {
    return country.name;
  }
}

/** A province's name in the reader's language ("Québec" in French). */
export const provinceDisplayName = (
  province: LocationProvince,
  locale: string
): string => province.nameTranslations?.[baseLanguage(locale)] || province.name;

/**
 * Country options sorted by their name in the reader's language, with
 * `firstCode` (the company's country, else Canada) on top.
 */
export function countryOptions(
  countries: LocationCountry[],
  locale: string,
  firstCode: string = DEFAULT_LOCATION_COUNTRY
): ComboBoxOption[] {
  const collator = new Intl.Collator(baseLanguage(locale));
  const first = firstCode.toUpperCase();
  return countries
    .map((country) => ({
      value: country.code,
      label: countryDisplayName(country, locale),
    }))
    .sort(
      (a, b) =>
        Number(b.value === first) - Number(a.value === first) ||
        collator.compare(a.label, b.label)
    );
}

/**
 * The short list of a country's provinces (older servers send every row with
 * no `selectable`, all kept), by name in the reader's language.
 */
export function provinceOptions(
  provinces: LocationProvince[],
  locale: string
): ComboBoxOption[] {
  const collator = new Intl.Collator(baseLanguage(locale));
  return provinces
    .filter((province) => province.selectable !== false)
    .map((province) => ({
      value: String(province.id),
      label: provinceDisplayName(province, locale),
    }))
    .sort((a, b) => collator.compare(a.label, b.label));
}

/** Form values of a stored location; blanks where it has none. */
export const locationFormValues = (location?: {
  countryCode?: string | null;
  provinceId?: number | string | null;
  city?: string | null;
}): LocationFormValues => ({
  countryCode: location?.countryCode ?? '',
  provinceId:
    location?.provinceId == null || location.provinceId === ''
      ? ''
      : String(location.provinceId),
  city: location?.city ?? '',
});

/** What the API takes: trimmed, and no province outside the five countries. */
export const locationPayload = (
  values: LocationFormValues
): LocationPayload => ({
  countryCode: values.countryCode,
  ...(isProvinceRequired(values.countryCode) && values.provinceId
    ? { provinceId: Number(values.provinceId) }
    : {}),
  city: values.city.trim(),
});

const message = (key: string) => () =>
  i18n.t(`location.validation.${key}`, { ns: TranslationNS.LibsCommon });

/**
 * Yup rules for `LocationFields`, the server's rules: a country, a province
 * for CA, US, AU, GB and FR, and a city. Spread into the form's own object.
 */
export const locationFieldsShape = () => ({
  countryCode: Yup.string().required(message('country_required')),
  provinceId: Yup.string().when('countryCode', {
    is: (countryCode: string | undefined) => isProvinceRequired(countryCode),
    then: (schema) => schema.required(message('province_required')),
    otherwise: (schema) => schema.optional(),
  }),
  city: Yup.string()
    .trim()
    .required(message('city_required'))
    .max(LOCATION_CITY_MAX_LENGTH, message('city_max')),
});

/** A stored location as the branch API returns it. */
export interface StoredLocation {
  countryCode?: string | null;
  provinceId?: number | null;
  city?: string | null;
  province?: LocationProvince | null;
}

/** What a stored location still lacks, by the server's own rules. */
export type LocationGap = 'country' | 'province' | 'city';

/**
 * Mirror of the backend `branchLocationGaps`: a province off the short list
 * (a GB county, a FR department) or of another country counts as missing.
 */
export function locationGaps(location: StoredLocation): LocationGap[] {
  const gaps: LocationGap[] = [];
  const countryCode = location.countryCode?.trim().toUpperCase() || '';
  if (!countryCode) gaps.push('country');
  if (
    countryCode &&
    isProvinceRequired(countryCode) &&
    (location.provinceId == null ||
      !location.province ||
      location.province.countryCode.toUpperCase() !== countryCode ||
      location.province.selectable === false)
  ) {
    gaps.push('province');
  }
  if (!location.city?.trim()) gaps.push('city');
  return gaps;
}

/** "Joliette, QC, Canada": city, province code and country, as known. */
export function locationSummary(
  location: StoredLocation,
  locale: string
): string {
  const countryCode = location.countryCode?.trim().toUpperCase() || '';
  const province =
    location.province &&
    location.province.countryCode.toUpperCase() === countryCode &&
    isProvinceRequired(countryCode)
      ? location.province.code.split('-').slice(1).join('-')
      : '';
  return [
    location.city?.trim(),
    province,
    countryCode
      ? countryDisplayName({ code: countryCode, name: countryCode }, locale)
      : '',
  ]
    .filter(Boolean)
    .join(', ');
}
